import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { FloatLoad } from '../types/account';
import { accountService } from '../services/api';
import FloatLoadDialog from '../components/FloatLoadDialog';
import {
  formatCurrency,
  formatDateTime,
  getFloatLoadTypeLabel,
  exportToCSV,
} from '../utils/accountUtils';

const FloatManagement: React.FC = () => {
  const navigate = useNavigate();
  const [floatLoads, setFloatLoads] = useState<FloatLoad[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Filter states
  const [partnerId, setPartnerId] = useState('');
  const [currency, setCurrency] = useState('');

  const loadFloatLoads = async (currentPage = page) => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: currentPage,
        size: rowsPerPage,
      };

      if (partnerId) params.partnerId = partnerId;
      if (currency) params.currency = currency;

      const response = await accountService.getFloatLoads(params);
      setFloatLoads(response.content);
      setTotalElements(response.totalElements);
    } catch (err: any) {
      console.error('Error loading float loads:', err);
      setError(err.response?.data?.message || 'Failed to load float loads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFloatLoads();
  }, [page, rowsPerPage]);

  const handleSearch = () => {
    setPage(0);
    loadFloatLoads(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFloatLoadSuccess = () => {
    setDialogOpen(false);
    loadFloatLoads();
  };

  const handleExport = () => {
    const exportData = floatLoads.map((load) => ({
      'Partner ID': load.partnerId,
      'Partner Name': load.partnerName || 'N/A',
      'Account Number': load.accountNumber,
      Currency: load.currency,
      Amount: load.amount,
      'Load Type': getFloatLoadTypeLabel(load.loadType),
      'Reference Number': load.referenceNumber,
      'Bank Reference': load.bankReference || 'N/A',
      'Deposit Date': load.depositDate || 'N/A',
      'Transaction Reference': load.transactionReference || 'N/A',
      Status: load.status,
      'Date Created': formatDateTime(load.dateCreated),
    }));
    exportToCSV(exportData, `float-loads-${new Date().toISOString().split('T')[0]}.csv`);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Float Management</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Load Float
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={floatLoads.length === 0}
          >
            Export
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadFloatLoads()}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Partner ID"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              placeholder="Enter partner ID"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={currency}
                label="Currency"
                onChange={(e) => setCurrency(e.target.value)}
              >
                <MenuItem value="">All Currencies</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="ZWG">ZWG</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button fullWidth variant="contained" onClick={handleSearch}>
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setPartnerId('');
                setCurrency('');
                setPage(0);
                loadFloatLoads(0);
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Float Loads Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date Created</TableCell>
                <TableCell>Partner</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Load Type</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Transaction Ref</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : floatLoads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography color="text.secondary">No float loads found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                floatLoads.map((load) => (
                  <TableRow key={load.id} hover>
                    <TableCell>{formatDateTime(load.dateCreated)}</TableCell>
                    <TableCell>
                      {load.partnerName ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => navigate(`/partners/${load.partnerId}`)}
                        >
                          {load.partnerName}
                        </Link>
                      ) : (
                        `ID: ${load.partnerId}`
                      )}
                    </TableCell>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/accounts/${load.accountNumber}`)}
                      >
                        {load.accountNumber}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Chip label={load.currency} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium" color="success.main">
                        {formatCurrency(load.amount, load.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getFloatLoadTypeLabel(load.loadType)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{load.referenceNumber}</Typography>
                      {load.bankReference && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Bank: {load.bankReference}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {load.transactionReference ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => {
                            // Navigate to transaction detail if we have the ID
                            // For now, just display it
                          }}
                        >
                          {load.transactionReference}
                        </Link>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={load.status}
                        size="small"
                        color={load.status === 'COMPLETED' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            // Navigate to float load detail or show details dialog
                          }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Float Load Dialog */}
      <FloatLoadDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={handleFloatLoadSuccess}
      />
    </Box>
  );
};

export default FloatManagement;
